import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Typography, Tag, Select, Space, Button, Modal, Input, InputNumber, Popconfirm, Checkbox, DatePicker, Descriptions, message } from '../../ui';
import { Plus } from 'lucide-react';
import { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { usePermission, useAuthStore } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIst, formatIstDate } from '../../lib/time';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const STATUS_COLOR: Record<string, string> = { REQUESTED: 'gold', APPROVED: 'blue', DISBURSED: 'green', REJECTED: 'red' };
const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'];

// Short role labels for the "by-whom" capsules.
const ROLE_LABEL: Record<string, string> = {
  SALES_PERSON: 'SP', SALES_HEAD: 'SH', ACCOUNTS: 'Accounts', ADMIN: 'Admin', SUPER_ADMIN: 'SA',
};
const roleTag = (role?: string | null) =>
  role ? <Tag tone="blue" style={{ marginInlineEnd: 0 }}>{ROLE_LABEL[role] ?? role.replace(/_/g, ' ')}</Tag> : null;
const byCell = (name?: string | null, role?: string | null, at?: string | null) =>
  name
    ? <Space direction="vertical" size={0}><Space size={4} wrap>{name}{roleTag(role)}</Space>{at && <span style={{ color: '#888', fontSize: 12 }}>{formatIstDate(at)}</span>}</Space>
    : <span style={{ color: '#ccc' }}>—</span>;

// Advance approval queue: rep requests → manager/admin approves → admin disburses (posts the
// ADVANCE_IN ledger row that moves money into the rep's wallet).
export function AdvanceRequestsPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  // Gate by the actual permission (not just role) — backend requires ADVANCE.REQUEST.
  const canRequest = usePermission('ADVANCE.REQUEST') && (role === 'SALES_PERSON' || role === 'SALES_HEAD');
  const canApprove = usePermission('ADVANCE.APPROVE');
  const canDisburse = usePermission('EXPENSE.APPROVE.ACCT');

  const canSeeAll = canApprove || canDisburse || role === 'SALES_HEAD';
  const [status, setStatus] = useState<string | undefined>();
  const [showHistory, setShowHistory] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [repId, setRepId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [disburseFor, setDisburseFor] = useState<any | null>(null);

  // Sales-person options for the filter (managers/admins only).
  const { data: salesPersons } = useQuery({
    queryKey: ['advance-salespersons'],
    queryFn: () => request<any[]>({ url: '/wallets' }),
    enabled: canSeeAll,
  });

  // Default to active (still-actionable) requests; the history toggle / a status / search /
  // date filter widens it to the full history.
  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    else if (!showHistory && !search.trim() && !range) p.active = 'true';
    if (repId) p.repId = repId;
    if (search.trim()) p.search = search.trim();
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    return p;
  }, [status, showHistory, repId, search, range]);
  const { data, isLoading } = useQuery({
    queryKey: ['advance-requests', params],
    queryFn: () => request<any[]>({ url: '/advance-requests', params }),
    placeholderData: keepPreviousData,
  });

  const act = useMutation({
    mutationFn: ({ id, action, data }: { id: string; action: string; data?: any }) =>
      request({ url: `/advance-requests/${id}/${action}`, method: 'PATCH', data }),
    onSuccess: () => { message.success('Done'); qc.invalidateQueries({ queryKey: ['advance-requests'] }); qc.invalidateQueries({ queryKey: ['wallets'] }); qc.invalidateQueries({ queryKey: ['badges'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'requestNo', title: 'Request No', dataIndex: 'requestNo', sortable: true, alwaysVisible: true },
    {
      key: 'repName', title: 'Sales Person', dataIndex: 'repName', sortable: true, width: 180,
      render: (v: any, r: any) => <Space size={4} wrap><Tag tone="blue" style={{ marginInlineEnd: 0 }}>{v ?? `#${r.repId}`}</Tag>{roleTag(r.repRole)}</Space>,
    },
    { key: 'amount', title: 'Amount', dataIndex: 'amount', sortable: true, align: 'right', render: (v: any) => <b>{inr2(v)}</b> },
    { key: 'purpose', title: 'Purpose', dataIndex: 'purpose', ellipsis: true, render: (v: any) => v || '—' },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, render: (s: string) => <Tag tone={STATUS_COLOR[s] as any}>{s}</Tag> },
    { key: 'createdAt', title: 'Requested', dataIndex: 'createdAt', sortable: true, render: (v: string) => formatIstDate(v) },
    { key: 'approvedByName', title: 'Approved by', dataIndex: 'approvedByName', width: 180, render: (_: any, r: any) => byCell(r.approvedByName, r.approvedByRole, r.approvedAt) },
    {
      key: 'disbursed', title: 'Disbursed', dataIndex: 'disbursedAt', width: 200,
      render: (_: any, r: any) => r.status === 'DISBURSED'
        ? <Space direction="vertical" size={0}>
            <span style={{ color: '#1F8A4C' }}>{formatIstDate(r.disbursedAt)}</span>
            {r.paymentMode && <Tag style={{ marginInlineEnd: 0 }}>{r.paymentMode}{r.paymentReference ? ` · ${r.paymentReference}` : ''}</Tag>}
          </Space>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    { key: 'disbursedByName', title: 'Disbursed by', dataIndex: 'disbursedByName', defaultVisible: false, render: (_: any, r: any) => byCell(r.disbursedByName, r.disbursedByRole) },
    {
      key: 'paymentProofUrl', title: 'Proof', dataIndex: 'paymentProofUrl', align: 'center',
      render: (v: any) => v ? <a href={v} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>View</a> : <span style={{ color: '#ccc' }}>—</span>,
    },
    { key: 'rejectReason', title: 'Note', dataIndex: 'rejectReason', ellipsis: true, render: (v: any, r: any) => r.status === 'REJECTED' ? <span style={{ color: '#D9363E' }}>{v || 'Rejected'}</span> : '—' },
    {
      key: 'actions', title: 'Action', alwaysVisible: true, width: 230,
      render: (_: any, r: any) => (
        <Space wrap onClick={(e) => e.stopPropagation()}>
          {r.status === 'REQUESTED' && canApprove && (
            <>
              <Popconfirm title="Approve this request?" okText="Approve" onConfirm={() => act.mutate({ id: r.id, action: 'approve' })}>
                <Button size="sm" variant="primary">Approve</Button>
              </Popconfirm>
              <RejectButton onReject={(reason) => act.mutate({ id: r.id, action: 'reject', data: { reason } })} />
            </>
          )}
          {r.status === 'APPROVED' && canDisburse && (
            <Button size="sm" variant="primary" onClick={() => setDisburseFor(r)}>Disburse</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Advance Requests</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canRequest && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setRequestOpen(true)}>Request advance</Button>
          )}
        </div>
      </div>
      <Card>
        <DataTable
          storageKey="advance-requests-v2"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          expandable={{ expandedRowRender: (r: any) => <RequestDetailPanel req={r} />, expandRowByClick: true }}
          toolbarLeft={
            <Space wrap>
              <Input placeholder="Request no / purpose" style={{ width: 200 }}
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              {canSeeAll && (
                <Select showSearch optionFilterProp="label" allowClear placeholder="Sales person" style={{ width: 200 }}
                  value={repId} onChange={setRepId}
                  options={(salesPersons ?? []).map((s: any) => ({ value: String(s.repId), label: s.repName ?? `#${s.repId}` }))} />
              )}
              <Select placeholder="Status" allowClear style={{ width: 160 }} value={status} onChange={setStatus}
                options={Object.keys(STATUS_COLOR).map((s) => ({ value: s, label: s }))} />
              <DatePicker.RangePicker disabledDate={disableFuture} value={range as any} onChange={(v) => setRange(v as any)} />
              <Checkbox checked={showHistory} onCheckedChange={(checked) => setShowHistory(checked)} label="Show full history" />
            </Space>
          }
        />
      </Card>

      <RequestModal open={requestOpen} onClose={() => setRequestOpen(false)} isHead={role === 'SALES_HEAD'} />
      {disburseFor && <DisburseModal req={disburseFor} onClose={() => setDisburseFor(null)} />}
    </div>
  );
}

// Full lifecycle of one advance request — requested → approved → disbursed (or rejected),
// with who/when/how at every step. Rendered inline as an expandable row's detail panel.
export function RequestDetailPanel({ req }: { req: any }) {
  const by = (name?: string | null, role?: string | null) =>
    name ? <Space size={4} wrap>{name}{roleTag(role)}</Space> : <span style={{ color: '#ccc' }}>—</span>;
  return (
    <Descriptions bordered size="small" column={3}>
      <Descriptions.Item label="Requested on">{formatIst(req.createdAt)}</Descriptions.Item>
      <Descriptions.Item label="Approved by">{by(req.approvedByName, req.approvedByRole)}</Descriptions.Item>
      <Descriptions.Item label="Approved on">{req.approvedAt ? formatIst(req.approvedAt) : '—'}</Descriptions.Item>
      <Descriptions.Item label="Disbursed by">{by(req.disbursedByName, req.disbursedByRole)}</Descriptions.Item>
      <Descriptions.Item label="Disbursed on">{req.disbursedAt ? formatIst(req.disbursedAt) : '—'}</Descriptions.Item>
      <Descriptions.Item label="Payment mode">{req.paymentMode ? `${req.paymentMode}${req.paymentReference ? ` · ${req.paymentReference}` : ''}` : '—'}</Descriptions.Item>
      <Descriptions.Item label="Payment proof">{req.paymentProofUrl ? <a href={req.paymentProofUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>View</a> : '—'}</Descriptions.Item>
      {req.status === 'REJECTED' && (
        <Descriptions.Item label="Reject reason" span={2}><span style={{ color: '#D9363E' }}>{req.rejectReason || '—'}</span></Descriptions.Item>
      )}
    </Descriptions>
  );
}

function RejectButton({ onReject }: { onReject: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <>
      <Button size="sm" variant="danger" onClick={() => setOpen(true)}>Reject</Button>
      <Modal open={open} onOpenChange={(o) => { if (!o) setOpen(false); }} title="Reject request"
        footer={
          <>
            <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="danger" disabled={!reason.trim()} onClick={() => { onReject(reason.trim()); setOpen(false); setReason(''); }}>Reject</Button>
          </>
        }>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (visible to the sales person)" />
      </Modal>
    </>
  );
}

function RequestModal({ open, onClose, isHead }: { open: boolean; onClose: () => void; isHead: boolean }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>();
  const [purpose, setPurpose] = useState('');
  const [forUserId, setForUserId] = useState<string | undefined>();

  const { data: team } = useQuery({
    queryKey: ['advance-team'],
    queryFn: () => request<any[]>({ url: '/advance-requests/team-members' }),
    enabled: open && isHead,
  });

  const save = useMutation({
    mutationFn: () => request({ url: '/advance-requests/request', method: 'POST', data: { amount: Number(amount), purpose, forUserId: isHead ? forUserId : undefined } }),
    onSuccess: () => { message.success('Advance requested'); qc.invalidateQueries({ queryKey: ['advance-requests'] }); onClose(); setAmount(undefined); setPurpose(''); setForUserId(undefined); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Request advance"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0) || !purpose.trim() || (isHead && !forUserId)} onClick={() => save.mutate()}>Request</Button>
        </>
      }>
      <Space direction="vertical" style={{ width: '100%' }}>
        {isHead && (
          <Select showSearch optionFilterProp="label" className="w-full" placeholder="For sales person (your team)"
            value={forUserId} onChange={setForUserId}
            options={(team ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))} />
        )}
        <InputNumber min={1} placeholder="Amount ₹" style={{ width: '100%' }} value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        <Input.TextArea rows={2} placeholder="Purpose (e.g. 3-day Pune-east tour)" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      </Space>
    </Modal>
  );
}

function DisburseModal({ req, onClose }: { req: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState('BANK_TRANSFER');
  const [ref, setRef] = useState('');
  const [proof, setProof] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: () => request({ url: `/advance-requests/${req.id}/disburse`, method: 'PATCH', data: { paymentMode: mode, paymentReference: ref || undefined, paymentProofUrl: proof } }),
    onSuccess: () => {
      message.success('Advance disbursed');
      qc.invalidateQueries({ queryKey: ['advance-requests'] });
      qc.invalidateQueries({ queryKey: ['wallets'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Disburse</Button>
        </>
      }
      title={<span>Disburse {req.requestNo} <Tag tone="blue">{req.repName ?? 'Sales Person'}</Tag></span>}>
      <Typography.Paragraph>
        Posts <b>{inr2(req.amount)}</b> as an <b>ADVANCE_IN</b> into the sales person's wallet.
      </Typography.Paragraph>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Space wrap align="center">
          <span style={{ color: '#888' }}>Paid via</span>
          <Select value={mode} onChange={setMode} style={{ width: 150 }} options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))} />
          <Input placeholder="Ref / UTR" value={ref} onChange={(e) => setRef(e.target.value)} style={{ width: 180 }} />
        </Space>
        <FileUploadField category="receipt" buttonLabel="Payment proof (receipt / screenshot)" value={proof} onChange={setProof} />
      </Space>
    </Modal>
  );
}
